import { tick } from "svelte";
/**
 * A class that contains a reactive `current` property
 *
 * Accepts an initial value, and an optional `start` function, which has a `set` function as its first argument,
 * which is used to update the value of the `current` property.
 *
 * @example
 * ```html
 * <script>
 * const now = new Readable(new Date(), (set) => {
 * 	const interval = setInterval(() => set(new Date()), 1000);
 * 	return () => clearInterval(interval);
 * });
 * </script>
 *
 * <p>{now.current.toLocaleTimeString()}</p>
 * ```
 *
 * @see {@link https://runed.dev/docs/utilities/readable}
 *
 */
export class Readable {
    #current = $state();
    #start;
    constructor(initialValue, start) {
        this.#current = initialValue;
        this.#start = start;
    }
    #subscribers = 0;
    #stop = null;
    get current() {
        if ($effect.tracking()) {
            $effect(() => {
                this.#subscribers++;
                if (this.#subscribers === 1) {
                    this.#subscribe(true);
                }
                return () => {
                    tick().then(() => {
                        this.#subscribers--;
                        if (this.#subscribers === 0) {
                            this.#unsubscribe();
                        }
                    });
                };
            });
        }
        else if (this.#subscribers === 0) {
            this.#subscribe(false);
            this.#unsubscribe();
        }
        return this.#current;
    }
    #subscribe(inEffect) {
        this.#stop =
            this.#start((value) => {
                this.#current = value;
            }, inEffect) ?? null;
    }
    #unsubscribe() {
        if (this.#stop === null)
            return;
        this.#stop();
        this.#stop = null;
    }
}
